import React, { createContext, useContext, useState, useEffect } from "react";
import glpiService from "../api/GLPIService";
import { useAuth } from "./AuthContext";

const TicketSearchContext = createContext();

export const TicketSearchProvider = ({ children }) => {
  const { sessionToken } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [filters, setFilters] = useState({
    status: 1, // Estado por defecto: Nuevo
    entity: null, // Entidad (null significa todas las entidades)
    priority: null, // Prioridad (opcional)
  });
  const [states, setStates] = useState([
    { id: 1, name: "Nuevo" },
    { id: 2, name: "En curso" },
    { id: 3, name: "Planificada" },
    { id: 4, name: "En espera" },
]);
  const [loading, setLoading] = useState(false);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1); // Página actual
  const [totalPages, setTotalPages] = useState(1); // Total de páginas
  const pageSize = 25; // Número de tickets por página
  
  // Función para buscar tickets basados en los filtros y la página actual
  const fetchTickets = async () => {
    if (!sessionToken) return;

    setLoading(true);
    try {
      const rangeStart = (currentPage - 1) * pageSize;
      const rangeEnd = rangeStart + pageSize - 1;

      const queryParams = {
        ...(filters.status !== "" && { // Filtrar por un estado específico si se especifica
            "criteria[0][field]": 12, // Campo Estado
            "criteria[0][searchtype]": "equals",
            "criteria[0][value]": filters.status,
          }),
          ...(filters.status === "" && { // Si el estado está vacío, excluir Cerrado y Resuelto
            "criteria[0][field]": 12,
            "criteria[0][searchtype]": "equals",
            "criteria[0][value]": "1", // 4 = Resuelto, 5 = Cerrado
            "criteria[1][link]": "OR",
            "criteria[1][field]": 12,
            "criteria[1][searchtype]": "equals",
            "criteria[1][value]": "2", // 4 = Resuelto, 5 = Cerrado
            "criteria[2][link]": "OR",
            "criteria[2][field]": 12,
            "criteria[2][searchtype]": "equals",
            "criteria[2][value]": "3", // 4 = Resuelto, 5 = Cerrado
            "criteria[3][link]": "OR",
            "criteria[3][field]": 12,
            "criteria[3][searchtype]": "equals",
            "criteria[3][value]": "3", // 4 = Resuelto, 5 = Cerrado                
          }),
        ...(filters.entity && {
          "criteria[4][link]": "AND",
          "criteria[4][field]": 80, // Campo Entidad
          "criteria[4][searchtype]": "contains",
          "criteria[4][value]": filters.entity,
        }),
        ...(filters.priority && {
          "criteria[5][link]": "AND",
          "criteria[5][field]": 3, // Campo Prioridad
          "criteria[5][searchtype]": "equals",
          "criteria[5][value]": filters.priority,
        }),
        sort: "id",
        range: `${rangeStart}-${rangeEnd}`,
      };

      const data = await glpiService.searchTickets(sessionToken, queryParams);

      setTickets(data.data || []);
      setTotalPages(Math.ceil(data.totalcount / pageSize)); // Calcula el total de páginas
    } catch (error) {
      console.error("Error al buscar tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Efecto para actualizar los tickets cuando cambian los filtros o la página actual
  useEffect(() => {
    fetchTickets();
    const interval = setInterval(() => {
      fetchTickets();
    }, 60000); // Cada 60 segundos

    return () => clearInterval(interval); // Limpia el intervalo al desmontar
  }, [filters, sessionToken, currentPage]); // Ejecuta el efecto cuando cambian filtros o sesión

  // Función para actualizar los filtros
  const updateFilter = (filterName, value) => {
    console.log(`Actualizando filtro ${filterName} con valor ${value}`);
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setCurrentPage(1); // Reinicia la página actual al cambiar los filtros
  };

  // Función para cambiar la página
  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <TicketSearchContext.Provider
      value={{
        tickets,
        filters,
        loading,
        currentPage,
        totalPages,
        updateFilter,
        changePage,
        states,
      }}
    >
      {children}
    </TicketSearchContext.Provider>
  );
};

export const useTicketSearch = () => useContext(TicketSearchContext);
